<template>
  <div ref="holder" class="prevent-select d-flex">
    <div class="btn-group-vertical h-100">
      <b-button
        size="sm"
        class="py-0 m-0"
        :style="palette_button_style"
        :title="$t('data_science_palette')"
        variant="outline-primary"
        :pressed="cur_palette_category === 'data_scientist'"
        @click="selected_palette_category = 'data_scientist'"
      >
        <span class="fas fa-analytics fa-fw" />
      </b-button>
      <b-button
        size="sm"
        class="py-0 m-0"
        :style="palette_button_style"
        :title="$t('data_engineering_palette')"
        variant="outline-primary"
        :pressed="cur_palette_category === 'data_engineer'"
        @click="selected_palette_category = 'data_engineer'"
      >
        <span class="fas fa-server fa-fw" />
      </b-button>
      <b-button
        size="sm"
        class="py-0 m-0"
        :style="palette_button_style"
        :title="$t('production_palette')"
        variant="outline-primary"
        :pressed="cur_palette_category === 'production'"
        @click="selected_palette_category = 'production'"
      >
        <span class="far fa-construction fa-fw" />
      </b-button>
      <b-button
        size="sm"
        class="py-0 m-0"
        :style="palette_button_style"
        :title="$t('monitoring_palette')"
        variant="outline-primary"
        :pressed="cur_palette_category === 'monitoring'"
        @click="selected_palette_category = 'monitoring'"
      >
        <span class="fas fa-binoculars fa-fw" />
      </b-button>
    </div>
    <div
      class="d-flex flex-wrap"
      :style="{
        width: elements_size.palette_width + 'px',
      }"
    >
      <div
        v-for="(brick_type, index) in brick_types"
        :key="brick_type.uid"
        :ref="'brick' + index"
        class="synaplus-cosmo-workflow-palette-brick brick border-top border-left border-right border-bottom border-darkblue d-flex align-items-center justify-content-center"
        :class="background_class(brick_type)"
        :style="{
          width: elements_size.brick_width + 'px',
          height: elements_size.brick_height + 'px',
        }"
        @mouseenter="onmouseenter(brick_type)"
        @mouseleave="onmouseleave(brick_type)"
      >
        <div
          v-if="brick_type !== 'none'"
          ref="symbolholder"
          class="d-flex fa-stack align-items-center"
          :class="symbolholderclasses(brick_type)"
        >
          <div
            class="p-0 d-flex fa-stack-1x justify-content-center"
            :style="{
              'font-size': elements_size.presymbol_fontsize + 'px',
            }"
          >
            <span :class="iconbackclasses(brick_type)" />
          </div>
          <div
            class="p-0 d-flex fa-stack-1x justify-content-center"
            :style="{
              'font-size': elements_size.symbol_fontsize + 'px',
            }"
          >
            <span :class="iconfrontclasses(brick_type)" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<i18n>
{
  "en": {
    "data_science_palette": "Data Science palette",
    "data_engineering_palette": "Data Engineering palette",
    "production_palette": "Production palette",
    "monitoring_palette": "Monitoring palette"
  },
  "fr": {
    "data_science_palette": "Palette de Data Science",
    "data_engineering_palette": "Palette de Data Engineering",
    "production_palette": "Palette de mise en production",
    "monitoring_palette": "Palette de monitoring"
  }
}
</i18n>

<style lang="scss" scoped>
@import './../custom-bootstrap.scss';

.grayed {
  color: $gray-400;
}

.prevent-select {
  user-select: none;
}

.holder {
  touch-action: none;
}
.brick {
  text-align: center;
  transition: all 0.3s ease;
}
.synaplus-cosmo-workflow-palette-brick.blued:hover {
  background-color: #6ec6ff;
}
</style>

<script>
import _ from 'lodash'

import Vue from 'vue'
import { mapState, mapGetters } from 'vuex'

import { icon_from_name } from './workflow_common.js'

import { WorkflowPaletteHandler } from './workflow_core.vue'
import {
  subscription_mixin,
  brick_mixin,
  project_mixin,
} from '@/components/mixin.js'

// *****************************************************************************

const palette_categories = {
  generic: (
    'sourcefile filter dispatch unique ' +
    'hstack colrenamer addconst addid ' +
    'vstack manualfe aggregation history ' +
    'historyfe modelgen result finish'
  ).split(' '),
  datasource: (
    'sourcefile database none result ' +
    'none none none none ' +
    'none none none none ' +
    'none none none none'
  ).split(' '),
  data_engineer: (
    'sourcefile database dispatch none ' +
    'hstack vstack none none ' +
    'filter colrenamer none none ' +
    'aggregation modelgen result finish'
  ).split(' '),
  data_scientist: (
    'sourcefile dispatch result unique ' +
    'hstack vstack history historyfe ' +
    'filter colrenamer addconst addid ' +
    'aggregation modelgen manualfe finish'
  ).split(' '),
  production: (
    'sourcefile database metavariables none ' +
    'api_input api_table api_output none ' +
    'none none none none ' +
    'none none result finish'
  ).split(' '),
  monitoring: (
    'probecolumns probedataset probemodel none ' +
    'none none none none ' +
    'none none none none ' +
    'none modelgen none none'
  ).split(' '),
  edit_brick: (
    'none none none modify ' +
    'none none none none ' +
    'none none none none ' +
    'delete none none cancel'
  ).split(' '),
  edit_connector: (
    'none none none none ' +
    'none none none none ' +
    'none none none none ' +
    'delete none none cancel'
  ).split(' '),
  edit_brick_changes_disabled: (
    'none none none modify ' +
    'none none none none ' +
    'none none none none ' +
    'none none none cancel'
  ).split(' '),
  edit_connector_changes_disabled: (
    'none none none none ' +
    'none none none none ' +
    'none none none none ' +
    'none none none cancel'
  ).split(' '),
}

export default {
  name: 'WorkflowPalette',
  components: {},
  mixins: [subscription_mixin, brick_mixin, project_mixin],
  props: ['workflow_interface', 'options'],
  data() {
    let res = {
      uid: _.uniqueId('uniqueid'),
      handler: undefined,
      selected_palette_category: 'data_scientist',
    }
    return res
  },
  computed: {
    // project_id() {
    //   let route = this.$route.path
    //   let regexp = new RegExp('(\\d)*/workflow')
    //   return Number(route.match(regexp)[0].split('/')[0])
    // },
    // cur_project() {
    //   let res =
    //     (this.automl_projects_undeleted_object.projects || {})[
    //       this.project_id
    //     ] || {}
    //   return res
    // },
    cur_palette_category() {
      let res = this.selected_palette_category
      if (this.options.selected.length !== 0) {
        let is_not_connector = _.isUndefined(
          this.connectors_undeleted.connectors[this.options.selected[0]]
        )
        if (this.options.selected.length === 1 && is_not_connector) {
          if (this.changes_disabled) {
            res = 'edit_brick_changes_disabled'
          } else {
            res = 'edit_brick'
          }
        } else {
          if (this.changes_disabled) {
            res = 'edit_connector_changes_disabled'
          } else {
            res = 'edit_connector'
          }
        }
      }
      return res
    },

    brick_type_names() {
      return palette_categories[this.cur_palette_category]
    },

    brick_types() {
      let brick_types = []
      for (const [i, value] of this.brick_type_names.entries()) {
        brick_types.push({
          uid: i,
          name: value,
        })
      }
      return brick_types
    },

    palette_button_style() {
      let res = {
        'font-size': this.elements_size.info_panel_button_fontsize + 'px',
      }

      return res
    },

    connectors_undeleted() {
      return this.automl_connectors_undeleted_object_per_project(
        this.project_id
      )
    },

    ...mapState(['elements_size']),
    ...mapGetters(['automl_connectors_undeleted_object_per_project']),
  },
  mounted() {
    this.handler = new WorkflowPaletteHandler(this)
    this.workflow_interface.install(this.handler)
  },
  beforeDestroy() {
    this.handler.stop()
    this.workflow_interface.uninstall(this.handler)
  },
  methods: {
    brick_is_disabled(brick_type) {
      return (
        this.changes_disabled &&
        brick_type.name !== 'modify' &&
        brick_type.name !== 'cancel'
      )
    },

    background_class(brick_type) {
      let res = []
      if (this.brick_is_disabled(brick_type)) {
        res.push('grayed')
      } else {
        res.push('blued')
      }
      return res
    },
    symbolholderclasses(brick_type) {
      let res = []
      if (this.brick_is_disabled(brick_type)) {
        res.push('grayed')
      }
      return res
    },
    iconbackclasses(brick_type) {
      let t = icon_from_name(brick_type.name)
      let res = _.cloneDeep(t.paletteiconbackclasses)
      if (this.brick_is_disabled(brick_type)) {
        res.pop()
        res.push('grayed')
      }
      return res
    },
    iconfrontclasses(brick_type) {
      let t = icon_from_name(brick_type.name)
      let res = _.cloneDeep(t.iconfrontclasses)
      if (this.brick_is_disabled(brick_type)) {
        res.push('grayed')
      }
      return res
    },

    onmouseenter(brick_type) {
      this.workflow_interface.tooltip = brick_type
    },
    onmouseleave() {
      this.workflow_interface.tooltip = undefined
    },
  },
}
</script>
